import { useCallback, useEffect, useState } from "react";
import {
  api,
  type ApiComment,
  type ApiCommentPosition,
  type CreateCommentInput,
} from "../lib/api";

type Args =
  | { mode: "share"; shareToken: string; projectId: string; pageUrl: string | null }
  | { mode: "owner"; projectId: string; shareToken: string; pageUrl: string | null };

type State =
  | { status: "loading" }
  | { status: "ready"; comments: ApiComment[] }
  | { status: "error"; message: string };

// Centralises the comment list + write paths for both the owner-side
// project page and the public reviewer-side share page. Reads go through
// the surface that matches the current user (owner endpoint when authed
// to avoid leaking write permissions through GET, share endpoint for
// reviewers). Writes always go through the share-token surface so the
// server has a single creation code path.
export function useComments(args: Args) {
  const [state, setState] = useState<State>({ status: "loading" });

  const reload = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const res =
        args.mode === "owner"
          ? await api.listCommentsByProject(args.projectId)
          : await api.listCommentsByShare(args.shareToken);
      setState({ status: "ready", comments: res.comments });
    } catch (err) {
      setState({
        status: "error",
        message: (err as Error).message ?? "Failed to load comments",
      });
    }
  }, [args.mode, args.shareToken, args.projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (input: CreateCommentInput) => {
      const res = await api.createComment(args.shareToken, input);
      setState((prev) =>
        prev.status === "ready"
          ? { status: "ready", comments: [...prev.comments, res.comment] }
          : { status: "ready", comments: [res.comment] },
      );
      return res.comment;
    },
    [args.shareToken],
  );

  const resolve = useCallback(
    async (commentId: string) => {
      if (args.mode !== "owner") throw new Error("only the owner can resolve");
      const res = await api.resolveComment(args.projectId, commentId);
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              comments: prev.comments.map((c) => (c.id === commentId ? res.comment : c)),
            }
          : prev,
      );
    },
    [args.mode, args.projectId],
  );

  const reopen = useCallback(
    async (commentId: string) => {
      if (args.mode !== "owner") throw new Error("only the owner can reopen");
      const res = await api.reopenComment(args.projectId, commentId);
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              comments: prev.comments.map((c) => (c.id === commentId ? res.comment : c)),
            }
          : prev,
      );
    },
    [args.mode, args.projectId],
  );

  const remove = useCallback(
    async (commentId: string) => {
      if (args.mode !== "owner") throw new Error("only the owner can delete");
      await api.deleteComment(args.projectId, commentId);
      // Cascade reply removal locally so the sidebar updates without a reload.
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              comments: prev.comments.filter(
                (c) => c.id !== commentId && c.parentId !== commentId,
              ),
            }
          : prev,
      );
    },
    [args.mode, args.projectId],
  );

  const edit = useCallback(
    async (commentId: string, body: string, authorEmail?: string) => {
      let updated: ApiComment;
      if (args.mode === "owner") {
        const res = await api.editCommentByOwner(args.projectId, commentId, body);
        updated = res.comment;
      } else {
        if (!authorEmail) throw new Error("authorEmail required for share-token edit");
        const res = await api.editCommentByShare(args.shareToken, commentId, {
          body,
          authorEmail,
        });
        updated = res.comment;
      }
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              comments: prev.comments.map((c) => (c.id === commentId ? updated : c)),
            }
          : prev,
      );
    },
    [args.mode, "projectId" in args ? args.projectId : null, "shareToken" in args ? args.shareToken : null], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Optimistic drag-to-reposition: snap the pin to the new spot in
  // local state immediately so the iframe re-renders without lag, then
  // call the API. On failure revert to the snapshotted position.
  const updatePosition = useCallback(
    async (commentId: string, position: ApiCommentPosition) => {
      if (args.mode !== "owner") {
        throw new Error("only the owner can move pins");
      }
      let snapshot: ApiCommentPosition | null = null;
      setState((prev) => {
        if (prev.status !== "ready") return prev;
        return {
          status: "ready",
          comments: prev.comments.map((c) => {
            if (c.id !== commentId) return c;
            snapshot = c.position;
            return { ...c, position };
          }),
        };
      });
      try {
        const res = await api.updateCommentPosition(
          args.projectId,
          commentId,
          position,
        );
        setState((prev) =>
          prev.status === "ready"
            ? {
                status: "ready",
                comments: prev.comments.map((c) =>
                  c.id === commentId ? res.comment : c,
                ),
              }
            : prev,
        );
      } catch (err) {
        if (snapshot) {
          const reverted = snapshot;
          setState((prev) =>
            prev.status === "ready"
              ? {
                  status: "ready",
                  comments: prev.comments.map((c) =>
                    c.id === commentId ? { ...c, position: reverted } : c,
                  ),
                }
              : prev,
          );
        }
        throw err;
      }
    },
    [args.mode, "projectId" in args ? args.projectId : null], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Comments scoped to the page the iframe is currently showing — used for
  // both the sidebar list and the pin rendering payload sent to the overlay.
  const forCurrentPage =
    state.status === "ready" && args.pageUrl
      ? state.comments.filter((c) => c.pageUrl === args.pageUrl)
      : state.status === "ready"
        ? state.comments
        : [];

  const otherPagesCount =
    state.status === "ready" && args.pageUrl
      ? state.comments.filter((c) => c.pageUrl !== args.pageUrl).length
      : 0;

  return {
    state,
    reload,
    create,
    resolve,
    reopen,
    remove,
    edit,
    updatePosition,
    forCurrentPage,
    otherPagesCount,
  };
}
